import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import QMSLayout from "../components/QMSLayout";
import { API } from "../App";
import { useAuth } from "../App";
import { Plus, Truck, Shield, AlertTriangle, CheckCircle, Search } from "lucide-react";

const SuppliersPage = () => {
  const { t } = useTranslation('operations');
  const { isAdmin, isAuditor } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sectorFilter, setSectorFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    sector: "life-sciences",
    risk_level: "medium",
    contact_email: "",
    notes: ""
  });

  const fetchSuppliers = async () => {
    try {
      let url = `${API}/suppliers`;
      const params = [];
      if (sectorFilter !== "all") params.push(`sector=${sectorFilter}`);
      if (statusFilter !== "all") params.push(`status=${statusFilter}`);
      if (params.length) url += `?${params.join("&")}`;
      
      const response = await axios.get(url, { withCredentials: true });
      setSuppliers(response.data);
    } catch (error) {
      console.error("Error fetching suppliers:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuppliers();
  }, [sectorFilter, statusFilter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/suppliers`, formData, { withCredentials: true });
      setShowModal(false);
      setFormData({ name: "", sector: "life-sciences", risk_level: "medium", contact_email: "", notes: "" });
      fetchSuppliers();
    } catch (error) {
      console.error("Error creating supplier:", error);
    }
  };

  const handleStatusUpdate = async (supplierId, newStatus) => {
    try {
      await axios.put(`${API}/suppliers/${supplierId}`, { qualification_status: newStatus }, { withCredentials: true });
      fetchSuppliers();
    } catch (error) {
      console.error("Error updating supplier:", error);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: "badge-amber",
      qualified: "badge-green",
      conditional: "badge-blue",
      disqualified: "badge-red"
    };
    return badges[status] || "badge-gray";
  };

  const getRiskBadge = (risk) => {
    const badges = { low: "badge-green", medium: "badge-amber", high: "badge-red" };
    return badges[risk] || "badge-gray";
  };

  // KPIs
  const qualified = suppliers.filter(s => s.qualification_status === "qualified").length;
  const pending = suppliers.filter(s => s.qualification_status === "pending").length;
  const highRisk = suppliers.filter(s => s.risk_level === "high").length;

  return (
    <QMSLayout title={t('suppliers.title')}>
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="supplier-kpis">
        <div className="kpi-card" style={{ "--kpi-accent": "#36A9E1" }}>
          <p className="kpi-label">{t('suppliers.totalSuppliers')}</p>
          <p className="kpi-value">{suppliers.length}</p>
        </div>
        <div className="kpi-card" style={{ "--kpi-accent": "#4ADE80" }}>
          <p className="kpi-label">{t('suppliers.qualified')}</p>
          <p className="kpi-value">{qualified}</p>
        </div>
        <div className="kpi-card" style={{ "--kpi-accent": "#FBBF24" }}>
          <p className="kpi-label">{t('suppliers.pending')}</p>
          <p className="kpi-value">{pending}</p>
        </div>
        <div className="kpi-card" style={{ "--kpi-accent": "#F87171" }}>
          <p className="kpi-label">{t('suppliers.highRisk')}</p>
          <p className="kpi-value">{highRisk}</p>
        </div>
      </div>

      {/* Filters & Actions */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="filter-pills">
          {["all", "qualified", "pending", "conditional", "disqualified"].map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`filter-pill ${statusFilter === status ? "active" : ""}`}
            >
              {status === "all" ? "All Status" : status}
            </button>
          ))}
        </div>

        <select
          value={sectorFilter}
          onChange={(e) => setSectorFilter(e.target.value)}
          className="qms-select w-auto"
        >
          <option value="all">{t('suppliers.allSectors')}</option>
          <option value="life-sciences">{t('suppliers.lifeSciences')}</option>
          <option value="aerospace">{t('suppliers.aerospace')}</option>
          <option value="industrial">{t('suppliers.industrial')}</option>
          <option value="iot">{t('suppliers.iot')}</option>
        </select>

        {(isAdmin || isAuditor) && (
          <button onClick={() => setShowModal(true)} className="btn-primary ml-auto" data-testid="create-supplier-btn">
            <Plus size={18} /> {t('suppliers.addSupplier')}
          </button>
        )}
      </div>

      {/* Table */}
      <div className="qms-card overflow-hidden" data-testid="suppliers-table">
        <div className="overflow-x-auto">
          <table className="qms-table">
            <thead>
              <tr>
                <th>{t('suppliers.id')}</th>
                <th>{t('suppliers.name')}</th>
                <th>{t('suppliers.sector')}</th>
                <th>{t('suppliers.status')}</th>
                <th>{t('suppliers.riskLevel')}</th>
                <th>{t('suppliers.score')}</th>
                <th>{t('suppliers.lastAudit')}</th>
                <th>{t('suppliers.nextAudit')}</th>
                {(isAdmin || isAuditor) && <th>{t('suppliers.actions')}</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="text-center py-8 text-[#8B95A5]">{t('suppliers.loading')}</td></tr>
              ) : suppliers.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-8 text-[#8B95A5]">{t('suppliers.noSuppliers')}</td></tr>
              ) : (
                suppliers.map((supplier) => (
                  <tr key={supplier.supplier_id}>
                    <td className="font-mono text-[#38BDF8]">{supplier.supplier_id}</td>
                    <td>
                      <div>
                        <p className="text-white font-medium">{supplier.name}</p>
                        {supplier.contact_email && (
                          <p className="text-[#8B95A5] text-xs">{supplier.contact_email}</p>
                        )}
                      </div>
                    </td>
                    <td className="capitalize">{supplier.sector.replace("-", " ")}</td>
                    <td>
                      <span className={`badge ${getStatusBadge(supplier.qualification_status)} capitalize`}>
                        {supplier.qualification_status}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${getRiskBadge(supplier.risk_level)} capitalize`}>
                        {supplier.risk_level}
                      </span>
                    </td>
                    <td>
                      {supplier.score ? (
                        <span className={`font-semibold ${supplier.score >= 80 ? "text-[#4ADE80]" : "text-[#FBBF24]"}`}>
                          {supplier.score}%
                        </span>
                      ) : "-"}
                    </td>
                    <td>{supplier.last_audit_date || "-"}</td>
                    <td>{supplier.next_audit_date || "-"}</td>
                    {(isAdmin || isAuditor) && (
                      <td>
                        <div className="flex gap-2">
                          {supplier.qualification_status === "pending" && (
                            <button
                              onClick={() => handleStatusUpdate(supplier.supplier_id, "qualified")}
                              className="text-xs text-[#4ADE80] hover:underline"
                            >
                              {t('suppliers.qualify')}
                            </button>
                          )}
                          {supplier.qualification_status === "qualified" && (
                            <button
                              onClick={() => handleStatusUpdate(supplier.supplier_id, "conditional")}
                              className="text-xs text-[#FBBF24] hover:underline"
                            >
                              {t('suppliers.setConditional')}
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b border-[rgba(255,255,255,0.07)]">
              <h3 className="font-['Syne'] text-lg font-semibold text-white">{t('suppliers.addSupplier')}</h3>
              <button onClick={() => setShowModal(false)} className="text-[#8B95A5] hover:text-white">×</button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-[#8B95A5] text-sm mb-1">{t('suppliers.companyName')}</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="qms-input"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[#8B95A5] text-sm mb-1">{t('suppliers.sector')}</label>
                  <select
                    value={formData.sector}
                    onChange={(e) => setFormData({ ...formData, sector: e.target.value })}
                    className="qms-select"
                  >
                    <option value="life-sciences">{t('suppliers.lifeSciences')}</option>
                    <option value="aerospace">{t('suppliers.aerospace')}</option>
                    <option value="industrial">{t('suppliers.industrial')}</option>
                    <option value="iot">{t('suppliers.iot')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[#8B95A5] text-sm mb-1">{t('suppliers.riskLevel')}</label>
                  <select
                    value={formData.risk_level}
                    onChange={(e) => setFormData({ ...formData, risk_level: e.target.value })}
                    className="qms-select"
                  >
                    <option value="low">{t('suppliers.low')}</option>
                    <option value="medium">{t('suppliers.medium')}</option>
                    <option value="high">{t('suppliers.high')}</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[#8B95A5] text-sm mb-1">{t('suppliers.contactEmail')}</label>
                <input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  className="qms-input"
                />
              </div>
              <div>
                <label className="block text-[#8B95A5] text-sm mb-1">{t('suppliers.notes')}</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="qms-input min-h-[80px]"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button type="submit" className="btn-primary flex-1">{t('suppliers.addSupplier')}</button>
                <button type="button" onClick={() => setShowModal(false)} className="btn-ghost">{t('suppliers.cancel')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </QMSLayout>
  );
};

export default SuppliersPage;
